/**
 * syncService.js — Synchronizacja wyników gier offline z serwerem
 *
 * Uruchamia się automatycznie po odzyskaniu połączenia.
 * Uploaduje tylko wyniki (fame points, win/loss) — NIE pełny stan gry.
 */

import * as offlineDb from './offlineDb.js';

const SYNC_URL = '/api/offline-sync';
const MAX_ATTEMPTS = 3;

let _syncing = false;

/**
 * Zarejestruj Background Sync w Service Workerze (jeśli dostępny)
 */
export async function registerBackgroundSync() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    if ('sync' in reg) {
      await reg.sync.register('sync-offline-games');
    }
  } catch {
    // Background Sync niedostępny — sync zostanie wywołany przez online event
  }
}

/**
 * Synchronizuj wszystkie oczekujące wyniki gier offline
 * @param {string|null} authToken - JWT token użytkownika (lub null jeśli niezalogowany)
 * @returns {{ synced: number, failed: number }}
 */
export async function syncOfflineGames(authToken = null) {
  if (_syncing) return { synced: 0, failed: 0 };
  _syncing = true;

  let synced = 0;
  let failed = 0;

  try {
    const pending = await offlineDb.getPendingSyncs();
    if (pending.length === 0) return { synced: 0, failed: 0 };

    for (const record of pending) {
      if (record.attempts >= MAX_ATTEMPTS) {
        // Zbyt wiele prób — oznacz jako synced żeby nie blokować kolejki
        await offlineDb.markSynced(record.gameId);
        continue;
      }

      try {
        const headers = { 'Content-Type': 'application/json' };
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

        const res = await fetch(SYNC_URL, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            gameId: record.gameId,
            finishedAt: record.finishedAt,
            result: record.result,
          }),
        });

        if (res.ok || res.status === 409) {
          // 409 Conflict = duplikat — też traktujemy jako sukces
          await offlineDb.markSynced(record.gameId);
          synced++;
        } else {
          await offlineDb.incrementSyncAttempts(record.gameId);
          failed++;
        }
      } catch {
        await offlineDb.incrementSyncAttempts(record.gameId);
        failed++;
      }
    }
  } finally {
    _syncing = false;
  }

  return { synced, failed };
}

/**
 * Hook do wywołania przy evencie 'online'
 * Używane w useOnlineStatus lub bezpośrednio w GameContext
 */
export function setupOnlineSyncListener(getAuthToken) {
  const handler = async () => {
    // Poczekaj chwilę żeby upewnić się że połączenie jest stabilne
    await new Promise((r) => setTimeout(r, 2000));
    const token = getAuthToken ? getAuthToken() : null;
    await syncOfflineGames(token);
    await registerBackgroundSync();
  };

  window.addEventListener('online', handler);
  return () => window.removeEventListener('online', handler);
}
